import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'utils/axios';

// MUI imports
import {
    Alert,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';

// Project imports
import { useDispatch } from 'store';
import MainCard from 'ui-component/cards/MainCard';
import { showErrorAlert } from 'utils/global-functions';
import { openSnackbar } from 'store/slices/snackbar';
import RatingToggle from './RatingToggle';
import { buildTree, leavesOf } from './goalsTree';
import { PHONE_TAP_TARGET, SubjectLabel, isRated, termNotStarted, termStartLabel } from './constants';

const SAVE_FAILED = 'The goals could not be saved. Please try again.';

// The API's own refusals and faults are short plain-text messages. Anything else (a gateway's HTML page, an empty
// reply that the axios setup turns into "Wrong Services", a JSON body or a dropped connection) gets the general message.
const saveErrorMessage = (ex) => {
    const text = typeof ex === 'string' ? ex.trim() : '';
    if (!text || text === 'Wrong Services' || text.startsWith('<') || text.length > 300) return SAVE_FAILED;
    return text;
};

export default function GoalsPage() {
    const { state } = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // Shown above Save Goals, where a snackbar would cover the button the message asks the user to try again
    const [saveError, setSaveError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [details, setDetails] = useState({});
    const [ratings, setRatings] = useState({});
    // As loaded, so changing a rating and back again doesn't count as an unsaved change
    const [initialRatings, setInitialRatings] = useState({});
    const [standards, setStandards] = useState([]);

    // Back to the term these goals are for, not the current one
    const closeGoals = () => navigate(state?.termId ? `/assessments?termId=${state.termId}` : '/assessments');

    useEffect(() => {
        async function getGoals() {
            if (!state?.studentId || !state?.subject || !state?.termId) {
                showErrorAlert(dispatch, 'Please open Goals from the Assessments page.');
                closeGoals();
                return;
            }

            try {
                const { data } = await axios.get('/assessments/goals', {
                    params: { studentId: state.studentId, subject: state.subject, termId: state.termId }
                });
                setDetails(data);
                setStandards(data.standards);
                const initial = {};
                data.standards.forEach((s) => {
                    initial[s.id] = s.rating;
                });
                setRatings(initial);
                setInitialRatings(initial);
            } catch (ex) {
                showErrorAlert(dispatch, ex);
                closeGoals();
            }

            setLoading(false);
        }

        getGoals();
    }, []);

    const domains = useMemo(() => buildTree(standards), [standards]);
    const leaves = useMemo(() => leavesOf(domains.flatMap((d) => d.goals)), [domains]);
    const markedCount = leaves.filter((l) => isRated(ratings[l.id])).length;

    const setRating = (standardId, rating) => {
        setRatings((prev) => ({ ...prev, [standardId]: rating }));
    };

    // Unrated and NT look and save the same, so they count as equal here
    const dirty = leaves.some((l) => (ratings[l.id] ?? 0) !== (initialRatings[l.id] ?? 0));

    // Nothing can be saved before the term's first day
    const termState = { startDate: state?.termStartDate };
    const notStarted = termNotStarted(termState);
    // Until this term's goals are saved, the checklist is filled in from the most recent earlier term with saved goals.
    // Saving with no changes then records those ratings as this term's.
    const savedThisTerm = Boolean(details.goalsSaved);
    const prefillTerm = details.ratingsFromTerm;
    const canSave = !saving && !notStarted && markedCount > 0 && (dirty || !savedThisTerm);

    const onCloseClick = () => {
        if (dirty) {
            setDialogOpen(true);
        } else {
            closeGoals();
        }
    };

    const onSaveClick = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            // The full checklist, so this term holds a complete set. Ratings only, so this can't overwrite a type or
            // score saved on the Assessments page.
            await axios.post('/assessments/goals', {
                studentId: state.studentId,
                subject: state.subject,
                termId: state.termId,
                ratings: leaves.map((l) => ({ standardId: l.id, rating: ratings[l.id] ?? 0 }))
            });
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Goals saved',
                    variant: 'alert',
                    alert: { color: 'success' },
                    close: false
                })
            );
            closeGoals();
            return;
        } catch (ex) {
            setSaveError(saveErrorMessage(ex));
        }
        setSaving(false);
    };

    const renderLeafRow = (node, indent) => (
        <Stack
            key={node.id}
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={{ xs: 0.75, sm: 2 }}
            sx={{ pl: { xs: 0, sm: indent }, py: 0.75 }}
        >
            <Typography fontSize={13} sx={{ color: theme.palette.grey[700] }}>
                {node.code ? `${node.code}: ` : '• '}
                {node.text}
            </Typography>
            <RatingToggle value={ratings[node.id]} onChange={(v) => setRating(node.id, v)} disabled={saving || notStarted} />
        </Stack>
    );

    const renderNode = (node, depth) => {
        if (!node.children || node.children.length === 0) {
            return renderLeafRow(node, depth * 2);
        }

        return (
            <Paper
                key={node.id}
                variant="outlined"
                sx={{
                    p: { xs: 1, sm: 1.5 },
                    mb: 1.5,
                    ml: { xs: depth * 0.5, sm: depth * 2 },
                    backgroundColor: depth === 0 ? theme.palette.grey[50] : 'white'
                }}
            >
                <Typography fontWeight={600} fontSize={depth === 0 ? 14 : 13} color="black" sx={{ mb: 1 }}>
                    {node.code ? `${node.code}: ` : ''}
                    {node.text}
                </Typography>
                {node.children.map((child) => renderNode(child, depth + 1))}
            </Paper>
        );
    };

    const subjectLabel = SubjectLabel[state?.subject];

    return (
        <MainCard
            title={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box>{details.studentName ? `${details.studentName} - ${subjectLabel} - Term ${state?.term} Goals` : 'Goals'}</Box>
                    <Button size="small" startIcon={<ArrowBackIcon />} onClick={onCloseClick} disabled={saving} sx={PHONE_TAP_TARGET}>
                        Back to Assessments
                    </Button>
                </Box>
            }
            // Visible overflow lets the save bar stick to the bottom of the screen
            sx={{ overflow: 'visible' }}
        >
            {loading ? (
                <Backdrop sx={{ zIndex: (t) => t.zIndex.drawer + 1 }} open>
                    <CircularProgress color="primary" />
                </Backdrop>
            ) : (
                <>
                    <Typography fontSize={12} sx={{ color: theme.palette.grey[500], mb: 2 }}>
                        Group {details.groupName} · Grade {details.grade ?? '—'} · {details.providerName}
                    </Typography>

                    {notStarted && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Term {state.term} starts on {termStartLabel(termState)}. You can save goals from that day.
                            {!savedThisTerm && prefillTerm && ` These are the Term ${prefillTerm} ratings.`}
                        </Alert>
                    )}

                    {!notStarted && !savedThisTerm && prefillTerm && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Starting from Term {prefillTerm} ratings. Save to record them for Term {state.term}.
                        </Alert>
                    )}

                    {leaves.length === 0 && (
                        <Typography align="center" sx={{ py: 5 }} color="text.secondary">
                            No standards checklist has been entered yet for Grade {details.grade ?? '—'} {subjectLabel}. Please check back
                            later or contact the office.
                        </Typography>
                    )}

                    {domains.map((domain) => (
                        <Box key={domain.name} sx={{ mb: 3 }}>
                            <Typography fontSize={12} fontWeight={700} textTransform="uppercase" color="secondary.dark" sx={{ mb: 1 }}>
                                Standard Domain: {domain.name}
                            </Typography>
                            {domain.goals.map((goal) => renderNode(goal, 0))}
                        </Box>
                    ))}

                    <Box
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 2,
                            py: 1.5,
                            backgroundColor: 'background.paper',
                            borderTop: `1px solid ${theme.palette.grey[200]}`
                        }}
                    >
                        {saveError && (
                            <Alert severity="error" onClose={() => setSaveError(null)} sx={{ mb: 1.5 }}>
                                {saveError}
                            </Alert>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Typography fontSize={14} color="text.secondary">
                                {leaves.length > 0 && `${markedCount} of ${leaves.length} marked`}
                                {/* Why Save is off, when it isn't obvious */}
                                {/* Mid grey (#757575, as the score hint): text.secondary is too light for the one line that explains it */}
                                {leaves.length > 0 && !notStarted && markedCount === 0 && (
                                    <Box component="span" sx={{ color: grey[600] }}>
                                        {' · Mark at least one skill to save'}
                                    </Box>
                                )}
                            </Typography>
                            {/* The note beside it wraps instead of squeezing the button onto two lines on a phone */}
                            <Button
                                variant="contained"
                                startIcon={<CheckIcon />}
                                disabled={!canSave}
                                onClick={onSaveClick}
                                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                                Save Goals
                                {saving && (
                                    <CircularProgress
                                        size={22}
                                        sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-12px', marginLeft: '-12px' }}
                                    />
                                )}
                            </Button>
                        </Box>
                    </Box>
                </>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>Close Goals</DialogTitle>
                <DialogContent>You have changes that haven&apos;t been saved. Close without saving?</DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Keep editing</Button>
                    <Button color="error" onClick={closeGoals}>
                        Discard changes
                    </Button>
                </DialogActions>
            </Dialog>
        </MainCard>
    );
}
